import { memo, useCallback, useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { RATE_TYPE_LABELS } from "./CommissionTypeBadge";
import type { CommissionListFilters } from "../hooks/useCommissionList";
import type { RateType } from "../types/commission.types";

const RATE_TYPES: RateType[] = ["SUIT", "SHOES", "GOWN", "GRADUATION"];

interface CommissionFiltersProps {
  filters: CommissionListFilters;
  loading: boolean;
  onApply: (filters: CommissionListFilters) => void;
  onClear: () => void;
}

/**
 * Bloco de filtros server-side da listagem de comissões dentro da modal.
 *
 * Período (`startDate`/`endDate`) + multi-select de `rateType[]` em chips
 * toggle. Mantém estado interno (`draft`) — o Apply commita via `onApply`
 * e o Limpar reseta o tipo (mantendo o período exigido).
 *
 * @example
 * <CommissionFilters
 *   filters={list.filters}
 *   loading={list.loading}
 *   onApply={list.setFilters}
 *   onClear={() => list.setFilters({ startDate, endDate })}
 * />
 */
export const CommissionFilters = memo(function CommissionFilters({
  filters,
  loading,
  onApply,
  onClear,
}: CommissionFiltersProps) {
  const [draft, setDraft] = useState<CommissionListFilters>(filters);

  useEffect(() => {
    setDraft(filters);
  }, [filters]);

  const toggleType = useCallback((type: RateType) => {
    setDraft((prev) => {
      const current = prev.type ?? [];
      const next = current.includes(type)
        ? current.filter((t) => t !== type)
        : [...current, type];
      return { ...prev, type: next.length ? next : undefined };
    });
  }, []);

  const isInvalid =
    draft.startDate === "" ||
    draft.endDate === "" ||
    draft.endDate < draft.startDate;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="commission-filter-start">Data Inicial</Label>
          <Input
            id="commission-filter-start"
            type="date"
            value={draft.startDate}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, startDate: e.target.value }))
            }
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="commission-filter-end">Data Final</Label>
          <Input
            id="commission-filter-end"
            type="date"
            value={draft.endDate}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, endDate: e.target.value }))
            }
            disabled={loading}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Tipo</Label>
        <div className="flex flex-wrap gap-2">
          {RATE_TYPES.map((type) => {
            const selected = draft.type?.includes(type) ?? false;
            return (
              <button
                key={type}
                type="button"
                onClick={() => toggleType(type)}
                disabled={loading}
                aria-pressed={selected}
                className={cn(
                  "px-3 py-1.5 text-sm rounded-full border transition-colors",
                  selected
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:bg-accent",
                  loading && "opacity-50 cursor-not-allowed",
                )}
              >
                {RATE_TYPE_LABELS[type]}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={onClear}
          disabled={loading}
          className="gap-2"
        >
          <X className="w-4 h-4" />
          Limpar
        </Button>
        <Button
          onClick={() => onApply(draft)}
          disabled={loading || isInvalid}
          className="gap-2"
        >
          <Search className="w-4 h-4" />
          Aplicar
        </Button>
      </div>
    </div>
  );
});
